import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';
import { positionsAPI } from '../services/positionsAPI';
import { Position, User } from '../types';
import { API_BASE } from '../utils/fileUtils';
import PageHeader from '../components/PageHeader';
import '../styles/global.css';

const Profile: React.FC = () => {
  const { user: authUser, updateUser } = useAuth();
  const [user, setUser] = useState<User | null>(authUser);
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    email: '',
    city: '',
    phone_number: '',
    competencies: ''
  });
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [position, setPosition] = useState<Position | null>(null);

  useEffect(() => {
    // Используем authUser напрямую, не перезагружая данные
    if (authUser) {
      setUser(authUser);
      if (authUser.avatar_path) {
        setAvatarPreview(`${API_BASE}/uploads/${authUser.avatar_path}`);
      }

      // Инициализируем форму редактирования
      setEditFormData({
        full_name: authUser.full_name || '',
        email: authUser.email || '',
        city: authUser.city || '',
        phone_number: authUser.phone_number || '',
        competencies: Array.isArray(authUser.competencies) ? authUser.competencies.join(', ') : authUser.competencies || ''
      });

      // Загружаем только данные о должности
      if (authUser.position_id) {
        loadPositionData(authUser.position_id);
      }
    }
  }, [authUser]);

  const loadPositionData = async (positionId: number) => {
    try {
      const positionData = await positionsAPI.getPosition(positionId);
      setPosition(positionData);
    } catch (posErr: any) {
      console.error('Failed to load position:', posErr);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;

    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      await usersAPI.uploadAvatar(avatarFile);
      setMessage('Аватар успешно обновлен!');
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка загрузки аватара');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setEditPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveEditPhoto = () => {
    setEditPhotoFile(null);
    setEditPhotoPreview('');
    const fileInput = document.getElementById('editPhotoUpload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setLoading(true);
      setError('');

      // Создаем FormData для отправки
      const formData = new FormData();
      formData.append('full_name', editFormData.full_name);
      formData.append('email', editFormData.email);
      if (editFormData.city) formData.append('city', editFormData.city);
      if (editFormData.phone_number) formData.append('phone_number', editFormData.phone_number);
      if (editFormData.competencies) formData.append('competencies', editFormData.competencies);
      if (editPhotoFile) formData.append('photo', editPhotoFile);

      const response = await fetch(`${API_BASE}/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка обновления профиля');
      }

      const updatedUser = await response.json();
      updateUser(updatedUser);
      setMessage('Профиль успешно обновлен!');
      setEditMode(false);

      // Обновляем превью если загрузили новую фото
      if (editPhotoFile) {
        setAvatarPreview(URL.createObjectURL(editPhotoFile));
      }

      // Сбрасываем форму фото
      setEditPhotoFile(null);
      setEditPhotoPreview('');

    } catch (err: any) {
      setError(err.message || 'Ошибка обновления профиля');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <PageHeader
          title="Мой профиль"
          actions={
            <button type="button" className="btn btn-primary" onClick={() => setEditMode(!editMode)}>
              {editMode ? 'Отмена' : 'Редактировать'}
            </button>
          }
        />

        {message && <div className="success">{message}</div>}
        {error && <div className="error">{error}</div>}

        <div className="text-center mb-4">
          <div className="mb-3">
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="Avatar"
                className="avatar-large"
                style={{ margin: '0 auto' }}
              />
            ) : (
              <div
                className="avatar-large"
                style={{
                  backgroundColor: 'var(--primary-orange)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-inverse)',
                  fontWeight: 'bold',
                  fontSize: '36px',
                  margin: '0 auto'
                }}
              >
                {user.full_name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          <div className="file-upload">
            <input
              type="file"
              id="avatar-upload"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="avatar-upload" style={{ cursor: 'pointer' }}>
              Выбрать фото для аватара
            </label>
          </div>

          {avatarFile && (
            <button
              onClick={handleAvatarUpload}
              className="btn btn-primary mt-3"
              disabled={loading}
            >
              {loading ? 'Загрузка...' : 'Загрузить аватар'}
            </button>
          )}
        </div>

        {editMode ? (
          <form onSubmit={handleEditSubmit}>
            <div className="form-group">
              <label className="form-label">Полное имя:</label>
              <input
                type="text"
                className="form-control"
                value={editFormData.full_name}
                onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email:</label>
              <input
                type="email"
                className="form-control"
                value={editFormData.email}
                onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Город:</label>
              <input
                type="text"
                className="form-control"
                value={editFormData.city}
                onChange={(e) => setEditFormData({...editFormData, city: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Номер телефона:</label>
              <input
                type="tel"
                className="form-control"
                value={editFormData.phone_number}
                onChange={(e) => setEditFormData({...editFormData, phone_number: e.target.value})}
                placeholder="+7 (999) 123-45-67"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Компетенции:</label>
              <input
                type="text"
                className="form-control"
                value={editFormData.competencies}
                onChange={(e) => setEditFormData({...editFormData, competencies: e.target.value})}
                placeholder="Управление командой, разработка, аналитика данных"
              />
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                Введите компетенции через запятую или любой удобный формат
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setEditMode(false)}
              >
                Отмена
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Сохранение...' : 'Сохранить изменения'}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div className="form-group">
              <label className="form-label">Имя пользователя:</label>
              <input
                type="text"
                className="form-control"
                value={user.username}
                readOnly
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email:</label>
              <input
                type="email"
                className="form-control"
                value={user.email}
                readOnly
              />
            </div>

            <div className="form-group">
              <label className="form-label">Полное имя:</label>
              <input
                type="text"
                className="form-control"
                value={user.full_name}
                readOnly
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
